import { useEffect } from "react";

import { useNavBarWrapperRef } from "../../navBarStore";

/**
 * Pins a floating panel at the bottom of the NavBar's scroll viewport when its
 * natural position would be below the visible area. Uses direct DOM manipulation
 * with position:fixed to avoid the one-frame-behind jitter caused by React state
 * updates in scroll handlers.
 *
 * @param {import("react").RefObject} containerRef - Ref to the absolutely-positioned container
 */
const useClampedBottom = (containerRef) => {
  const navBarWrapperRef = useNavBarWrapperRef();

  useEffect(() => {
    const container = containerRef.current;
    const scrollEl = navBarWrapperRef.current;
    if (!container || !scrollEl) return;

    const positionedAncestor = container.offsetParent;
    if (!positionedAncestor) return;

    const update = () => {
      const scrollRect = scrollEl.getBoundingClientRect();
      const ancestorRect = positionedAncestor.getBoundingClientRect();
      const overflow = ancestorRect.bottom - scrollRect.bottom;

      if (overflow > 0) {
        container.style.position = "fixed";
        container.style.bottom = `${window.innerHeight - scrollRect.bottom}px`;
        container.style.left = `${ancestorRect.left}px`;
        container.style.width = `${ancestorRect.width}px`;
        container.style.right = "auto";
      } else {
        container.style.position = "";
        container.style.bottom = "";
        container.style.left = "";
        container.style.width = "";
        container.style.right = "";
      }
    };

    const observer = new ResizeObserver(update);
    observer.observe(positionedAncestor);
    observer.observe(scrollEl);

    scrollEl.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    requestAnimationFrame(() => update("raf-initial"));

    return () => {
      observer.disconnect();
      scrollEl.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [containerRef, navBarWrapperRef]);
};

export default useClampedBottom;
